import React, { useEffect, useMemo, useState } from "react";
import {
  HABITAT_COLORS,
  type GameState,
  type Habitat,
  type Season,
  type Tile,
} from "../vowGameLogic";
import { PANEL_CLASS } from "./uiConstants";
import { ACTION_ICONS, TILE_STATUS_ICONS } from "./iconAssets";

type PostGameAnalysisSectionProps = {
  gameState: GameState;
};

const SEASON_LABELS: Record<Season, string> = {
  P: "Spring",
  E: "Summer",
  A: "Autumn",
  H: "Winter",
};

const getWaterLevelColor = (waterLevel: number): string => {
  if (waterLevel > 20) return "#2563eb";
  if (waterLevel > 0) return "#60a5fa";
  if (waterLevel > -20) return "#c08457";
  return "#6b4e33";
};

type TileActionSummary = {
  icons: Array<{ src: string; alt: string }>;
  description: string;
};

const getTileActionSummary = (tile: Tile, snapshotAnnee?: number): TileActionSummary => {
  const icons: Array<{ src: string; alt: string }> = [];
  const labels: string[] = [];

  if (tile.actions?.graze) {
    icons.push(ACTION_ICONS.graze);
    labels.push("Grazing");
  }
  if (tile.actions?.harvest) {
    icons.push(ACTION_ICONS.harvest);
    labels.push("Reed Harvest");
  }
  if (tile.actions?.hunt) {
    icons.push(ACTION_ICONS.hunt);
    labels.push("Hunt");
  }
  if (tile.hasNest) {
    icons.push(TILE_STATUS_ICONS.nest);
    labels.push("Nest Installed");
  }

  const isProtected =
    snapshotAnnee !== undefined
      ? tile.protectedUntilYear >= snapshotAnnee
      : tile.protectedUntilYear > 0;
  if (isProtected) {
    icons.push(TILE_STATUS_ICONS.protected);
    labels.push("Nature Reserve");
  }

  return {
    icons,
    description: labels.length > 0 ? labels.join(", ") : "No actions recorded",
  };
};

const renderTileActionsCell = (tile: Tile, snapshotAnnee?: number) => {
  const summary = getTileActionSummary(tile, snapshotAnnee);

  return (
    <div
      className="flex h-14 w-14 flex-wrap items-center justify-center gap-1 rounded border border-black/30 bg-white p-1"
      title={summary.description}
    >
      {summary.icons.length > 0 ? (
        summary.icons.map((icon) => (
          <img
            key={`${icon.src}-${icon.alt}`}
            src={icon.src}
            alt={icon.alt}
            className="h-5 w-5 object-contain"
          />
        ))
      ) : (
        <span className="text-xs text-slate-500">—</span>
      )}
    </div>
  );
};

const PostGameAnalysisSection: React.FC<PostGameAnalysisSectionProps> = ({ gameState }) => {
  const snapshots = useMemo(() => gameState.boardSnapshots, [gameState.boardSnapshots]);
  const [selectedIndex, setSelectedIndex] = useState<number>(
    snapshots.length > 0 ? snapshots.length - 1 : -1
  );

  useEffect(() => {
    if (snapshots.length === 0) {
      setSelectedIndex(-1);
      return;
    }

    setSelectedIndex((current) => {
      if (current < 0 || current >= snapshots.length) {
        return snapshots.length - 1;
      }
      return current;
    });
  }, [snapshots.length]);

  const selectedSnapshot = selectedIndex >= 0 ? snapshots[selectedIndex] : null;

  if (snapshots.length === 0) {
    return null;
  }

  return (
    <section className={`${PANEL_CLASS} p-6`}>
      <h2 className="text-center text-2xl font-bold text-slate-900">
        Post-Game Analysis
      </h2>

      <div className="mt-6" id="snapshot-section">
        <h3 className="text-center text-xl font-semibold text-slate-900">
          Board Snapshots
        </h3>
        <div className="mt-4 flex flex-col gap-6 lg:flex-row">
          <div className="lg:w-64">
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-700">
              Snapshots
            </p>
            <div className="mt-3 max-h-96 overflow-y-auto border-2 border-black bg-[#dcd6c8]">
              {snapshots.map((snapshot, index) => {
                const isSelected = index === selectedIndex;
                return (
                  <button
                    key={`${snapshot.title}-${index}`}
                    type="button"
                    className={`flex w-full flex-col gap-1 border-b border-black/20 px-4 py-3 text-left transition ${
                      isSelected
                        ? "bg-[#bcb7a8] text-slate-900"
                        : "bg-transparent text-slate-800 hover:bg-[#cfc9b8]"
                    }`}
                    onClick={() => setSelectedIndex(index)}
                  >
                    <span className="text-base font-semibold">{snapshot.title}</span>
                    <span className="text-xs uppercase tracking-wide text-slate-700">
                      View #{index + 1}
                    </span>
                    {snapshot.includesActions && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-700">
                        Master View
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedSnapshot && (
            <div className="flex-1 border-2 border-black bg-[#bcb7a8] p-4">
              <h4 className="text-center text-lg font-semibold text-slate-900">
                {selectedSnapshot.title}
              </h4>
              {selectedSnapshot.includesActions && (
                <p className="mt-1 text-center text-xs font-semibold uppercase tracking-wide text-purple-700">
                  Game Master Snapshot — all player actions visible
                </p>
              )}
              <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <SnapshotGrid
                  title="Habitats"
                  plateau={selectedSnapshot.plateau}
                  renderCell={(tile) => (
                    <span
                      className="block h-6 w-6 border border-black/20"
                      style={{ backgroundColor: HABITAT_COLORS[tile.habitat as Habitat] }}
                    />
                  )}
                />
                <SnapshotGrid
                  title="Player Actions"
                  plateau={selectedSnapshot.plateau}
                  renderCell={(tile) =>
                    renderTileActionsCell(tile, selectedSnapshot.annee ?? gameState.annee)
                  }
                />
                {(["P", "E", "A", "H"] as Season[]).map((season) => (
                  <SnapshotGrid
                    key={season}
                    title={`Water (${SEASON_LABELS[season]})`}
                    plateau={selectedSnapshot.plateau}
                    renderCell={(tile) => {
                      const waterLevel = tile.resources[`waterLevel${season}`] ?? 0;
                      return (
                        <span
                          className="flex h-6 w-6 items-center justify-center rounded border border-black/20 text-[10px] font-semibold text-slate-800"
                          style={{ backgroundColor: getWaterLevelColor(waterLevel) }}
                          title={`${SEASON_LABELS[season]} water level: ${waterLevel}`}
                        >
                          {waterLevel}
                        </span>
                      );
                    }}
                  />
                ))}
                <SnapshotGrid
                  title="Nest Success"
                  plateau={selectedSnapshot.plateau}
                  renderCell={(tile) => (
                    <span
                      className="flex h-6 w-6 items-center justify-center text-lg border border-black/60 bg-white"
                      title={tile.nestSuccessLastAct ? "Successful nest" : "No success"}
                    >
                      {tile.nestSuccessLastAct ? "✓" : ""}
                    </span>
                  )}
                />
              </div>
            </div>
          )}
          {!selectedSnapshot && (
            <div className="flex flex-1 items-center justify-center border-2 border-dashed border-black/40 bg-[#dcd6c8] p-4 text-sm text-slate-700">
              Select a snapshot to view details.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

type SnapshotGridProps = {
  title: string;
  plateau: Tile[];
  renderCell: (tile: Tile) => React.ReactNode;
};

const SnapshotGrid: React.FC<SnapshotGridProps> = ({ title, plateau, renderCell }) => (
  <div>
    <p className="text-center text-sm font-semibold text-slate-800">{title}</p>
    <div className="mt-2 grid grid-cols-4 gap-1">
      {plateau.map((tile) => (
        <div key={tile.id} className="flex items-center justify-center">
          {renderCell(tile)}
        </div>
      ))}
    </div>
  </div>
);

export default PostGameAnalysisSection;
